// TanStack Router, code-based routes (docs/CONTRACTS.md section 16). The shell builds the tree from the feature
// registry: a pathless frame route (session check, rail, top bar, omnibar) holds every feature's `app` routes, a
// pathless bare route holds the `bare` ones (sign-in, invite). The shell owns '/', not-found and the session check.
import { createRootRouteWithContext, createRoute, createRouter, Outlet, redirect, type AnyRoute } from '@tanstack/react-router'
import type { QueryClient } from '@tanstack/react-query'
import { features } from '../features/index.ts'
import { reloadIfUpdateReady } from '../lib/pwa.ts'
import { sessionQuery } from '../lib/session.ts'
import { kitchenSinkRoute } from '../routes/kitchen-sink/route.tsx'
import { BareRoute, FrameRoute, RootNotFound } from './Frame.tsx'
import { ErrorScreen, RouteSkeleton } from './screens.tsx'

export interface RouterContext {
  queryClient: QueryClient
}

export function createAppRouter(queryClient: QueryClient) {
  const rootRoute = createRootRouteWithContext<RouterContext>()({
    // Plan section E: an older client shows 'Update ready' and reloads at the next navigation (a move to another path).
    // The root's beforeLoad runs before every other route's and before any route chunk loads, so holding it here keeps
    // the page quiet while the new version takes over (lib/pwa.ts, reloadIfUpdateReady).
    beforeLoad: ({ location, preload }): Promise<never> | undefined => {
      const from = router.state.resolvedLocation
      return !preload && from && from.pathname !== location.pathname ? reloadIfUpdateReady() : undefined
    },
    component: Outlet,
    notFoundComponent: RootNotFound,
  })

  const frameRoute = createRoute({
    getParentRoute: () => rootRoute,
    id: 'frame',
    // The session check: GET /api/me, else /login. Offline, unavailable or a pending account render inside the frame instead.
    beforeLoad: async ({ context }) => {
      const session = await context.queryClient.fetchQuery(sessionQuery)
      if (session.kind === 'signed-out') throw redirect({ to: '/login', replace: true })
    },
    component: FrameRoute,
  })

  const bareRoute = createRoute({ getParentRoute: () => rootRoute, id: 'bare', component: BareRoute })

  const indexRoute = createRoute({
    getParentRoute: () => frameRoute,
    path: '/',
    beforeLoad: () => {
      throw redirect({ to: '/today', replace: true })
    },
  })

  const app: AnyRoute[] = [indexRoute, kitchenSinkRoute(frameRoute), ...features.flatMap((f) => f.app?.(frameRoute) ?? [])]
  const bare: AnyRoute[] = features.flatMap((f) => f.bare?.(bareRoute) ?? [])

  const routeTree = rootRoute.addChildren([frameRoute.addChildren(app), bareRoute.addChildren(bare)])

  const router = createRouter({
    routeTree,
    context: { queryClient },
    notFoundMode: 'root',
    defaultPreload: 'intent',
    defaultPendingMs: 300,
    defaultPendingMinMs: 0,
    defaultPendingComponent: () => <RouteSkeleton delay={0} />,
    defaultErrorComponent: ErrorScreen,
    defaultViewTransition: true,
    scrollRestoration: true,
  })
  return router
}
